import React, { useState } from "react";
import { motion } from "framer-motion";

import { HowItWorkCallToActionProps } from "data/types";
import { PRESTA_HOW_IT_WORKS } from "data/constants";

const HowItWorkCallToAction: React.FC<HowItWorkCallToActionProps> = ({
  title = PRESTA_HOW_IT_WORKS.title,
  titleHighlight = PRESTA_HOW_IT_WORKS.titleHighlight,
  steps = PRESTA_HOW_IT_WORKS.steps,
  gradientFrom = PRESTA_HOW_IT_WORKS.gradientFrom,
  gradientTo = PRESTA_HOW_IT_WORKS.gradientTo,
  stepCircleColor = "bg-eventToBeePink",
  bg = "bg-gradient-to-br",
}) => {
  const [currentImage, setCurrentImage] = useState(steps[0].image);

  const handleStepClick = (image: string) => {
    setCurrentImage(image);
  };

  // Animation variants for the image
  const imageVariants = {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
    exit: { opacity: 0, scale: 0.95, transition: { duration: 0.5 } },
  };

  return (
    <div
      className={`flex items-center justify-center p-6 ${bg} from-${gradientFrom} to-${gradientTo} dark:bg-neutral-800 dark:from-neutral-800  `}
    >
      <div className="flex flex-col w-full gap-8 mt-20 max-w-7xl md:flex-row">
        {/* Image Section */}
        <div className="flex-1">
          <div className="overflow-hidden rounded-lg">
            <div className="relative w-full">
              <motion.img
                key={currentImage}
                src={currentImage}
                alt="Step image"
                className="object-contain"
                variants={imageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              />
            </div>
          </div>
        </div>

        {/* Steps Section */}
        <div className="flex-1 space-y-8">
          <h2 className="text-xl font-extrabold leading-tight text-gray-900 md:text-3xl dark:text-white">
            {title} <span className="text-3xl md:text-4xl">{titleHighlight}</span>
          </h2>
          <div className="space-y-8">
            {steps.map((step) => (
              <div
                key={step.number}
                className="flex items-start cursor-pointer"
                onClick={() => handleStepClick(step.image)}
              >
                <div
                  className={`flex items-center justify-center ${step.circleWidth} ${step.circleHeight} h-12 mr-4 text-white rounded-full ${stepCircleColor}`}
                >
                  <span className="text-2xl font-bold">{step.number}</span>
                </div>
                <div className="space-y-2">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white">{step.title}</h2>
                  {step.description.map((desc, index) => (
                    <p key={index} className="text-sm text-gray-600 dark:text-white ">
                      {desc}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorkCallToAction;
